import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { LayoutDashboard, Users, GitBranch, X } from 'lucide-react';

// ─────────────────────────────────────────────
// CARD DATA
// ─────────────────────────────────────────────
const CARDS = [
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    label: 'Instant Status Visibility',
    title: 'Your entire compliance status. Four tiles. Five seconds.',
    description:
      '2 overdue. 2 due soon. 2 need acknowledgments. No digging. No guessing. Every admin walks in knowing exactly what to do today.',
    screenshot: '/assets/1_Dashboard.png',
    screenshotAlt: 'Veracord dashboard showing compliance status tiles',
    size: 'large',
  },
  {
    id: 'acknowledgements',
    icon: Users,
    label: 'Track Every Acknowledgment',
    title: 'Who read what. When. Timestamped.',
    description:
      'Proof for every staff member on every policy. The auditor asks — you click one button.',
    screenshot: '/assets/5_Policy_Ack_Page_1.png',
    screenshotAlt: 'Acknowledgements page showing staff timestamps',
    size: 'small',
  },
  {
    id: 'versions',
    icon: GitBranch,
    label: 'Immutable Audit Trail',
    title: 'Every version. Locked. Defensible.',
    description:
      'Every approval, every change, every upload. Timestamped and immutable. No ambiguity. No interpretation.',
    screenshot: '/assets/6_Policy_Version_screen.png',
    screenshotAlt: 'Version history showing locked policy versions',
    size: 'small',
  },
];

// ─────────────────────────────────────────────
// CSS TRANSITIONS — injected once
// Pure CSS transitions are bulletproof across
// all bundlers and environments. No Framer Motion
// dependency for the lightbox open/close.
// ─────────────────────────────────────────────
const LIGHTBOX_STYLES = `
  .vrc-lightbox-overlay {
    position: fixed;
    inset: 0;
    z-index: 9998;
    background: rgba(0,2,46,0.92);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    opacity: 0;
    transition: opacity 280ms ease;
    pointer-events: none;
  }
  .vrc-lightbox-overlay.vrc-visible {
    opacity: 1;
    pointer-events: all;
  }
  .vrc-lightbox-panel {
    width: 100%;
    max-width: 920px;
    border-radius: 20px;
    overflow: hidden;
    border: 1.5px solid rgba(51,153,153,0.35);
    box-shadow: 0 0 0 1px rgba(51,153,153,0.08), 0 32px 80px rgba(0,2,46,0.8), 0 8px 32px rgba(51,153,153,0.18);
    background: #00022e;
    display: flex;
    flex-direction: column;
    transform: scale(0.90) translateY(24px);
    opacity: 0;
    transition: transform 380ms cubic-bezier(0.23, 1, 0.32, 1), opacity 300ms ease;
  }
  .vrc-lightbox-overlay.vrc-visible .vrc-lightbox-panel {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  .vrc-close-btn {
    width: 32px; height: 32px; border-radius: 50%;
    background: rgba(255,255,255,0.04);
    border: 1.5px solid rgba(51,153,153,0.3);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: rgba(255,255,255,0.7);
    flex-shrink: 0; outline: none;
    transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
  }
  .vrc-close-btn:hover {
    transform: scale(1.1);
    border-color: rgba(51,153,153,0.7);
    background: rgba(255,255,255,0.08);
  }
  .vrc-screenshot-frame {
    position: relative;
    cursor: pointer;
  }
  .vrc-screenshot-hover {
    position: absolute;
    inset: 0;
    background: rgba(51,153,153,0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    opacity: 0;
    transition: opacity 200ms ease;
    pointer-events: none;
  }
  .vrc-screenshot-frame:hover .vrc-screenshot-hover {
    opacity: 1;
  }
`;

// ─────────────────────────────────────────────
// LIGHTBOX
// Uses CSS classes + requestAnimationFrame to
// trigger transitions reliably in all environments.
// ─────────────────────────────────────────────
const Lightbox = ({ card, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [imgReady, setImgReady] = useState(false);
  const overlayRef = useRef(null);
  const Icon = card.icon;

  // Trigger open animation on next frame so CSS transition fires
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Lock scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Animate out before unmounting
  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 380); // match transition duration
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className={`vrc-lightbox-overlay${visible && imgReady ? ' vrc-visible' : ''}`}
      onClick={handleClose}
    >
      <div
        className="vrc-lightbox-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Teal top accent */}
        <div style={{
          height: '3px',
          background: 'linear-gradient(90deg, #339999, #06b6d4, rgba(6,182,212,0))',
          flexShrink: 0,
        }} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: '1px solid rgba(51,153,153,0.15)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #02075d, #0a3d3d)',
              border: '1px solid rgba(51,153,153,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon style={{ width: '13px', height: '13px', color: '#ffffff' }} />
            </div>
            <span style={{
              fontFamily: 'Source Serif Pro, Georgia, serif',
              fontWeight: 700, fontSize: '14px',
              color: '#ffffff', letterSpacing: '-0.01em',
            }}>
              Veracord<span style={{ fontSize: '9px', fontWeight: 400, position: 'relative', top: '-8px', marginLeft: '1px', letterSpacing: 0, fontFamily: 'Inter, sans-serif' }}>™</span>
            </span>
            <div style={{ width: '1px', height: '16px', background: 'rgba(51,153,153,0.3)' }} />
            <span style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600, fontSize: '11px',
              letterSpacing: '0.15em', textTransform: 'uppercase',
              color: '#339999',
            }}>
              {card.label}
            </span>
          </div>

          <button className="vrc-close-btn" onClick={handleClose}>
            <X style={{ width: '14px', height: '14px' }} />
          </button>
        </div>

        {/* Screenshot */}
        <div style={{ background: '#f8fafc', flexGrow: 1 }}>
          <img
            src={card.screenshot}
            alt={card.screenshotAlt}
            loading="eager"
            onLoad={(e) => { e.currentTarget.style.opacity = '1'; setImgReady(true); }}
            style={{
              width: '100%', height: 'auto', display: 'block',
              maxHeight: '68vh',
              objectFit: 'contain', objectPosition: 'top center',
              imageRendering: '-webkit-optimize-contrast',
              opacity: 0,
              transition: 'opacity 350ms cubic-bezier(0.23, 1, 0.32, 1) 50ms',
            }}
          />
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid rgba(51,153,153,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600, fontSize: '13px',
            color: 'rgba(255,255,255,0.6)', margin: 0,
          }}>
            {card.title}
          </p>
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '11px', color: 'rgba(255,255,255,0.25)',
            letterSpacing: '0.05em',
          }}>
            ESC to close
          </span>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// SCREENSHOT FRAME
// ─────────────────────────────────────────────
const ScreenshotFrame = ({ src, alt, large = false, onClick }) => (
  <div
    className="vrc-screenshot-frame"
    onClick={onClick}
    style={{
      borderRadius: '10px', overflow: 'hidden',
      border: '1px solid rgba(15,23,42,0.1)',
      boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.08), 0 2px 12px rgba(0,0,0,0.06)',
      background: '#02075d',
      width: '100%', height: large ? '260px' : '180px',
    }}
  >
    <img
      src={src} alt={alt}
      style={{
        width: '100%', height: '100%',
        objectFit: 'contain', objectPosition: 'top center',
        display: 'block',
        imageRendering: '-webkit-optimize-contrast',
      }}
    />
    <div className="vrc-screenshot-hover">
      <div style={{
        background: 'rgba(0,2,46,0.88)',
        border: '1px solid rgba(51,153,153,0.45)',
        borderRadius: '99px', padding: '6px 14px',
        display: 'flex', alignItems: 'center', gap: '6px',
        backdropFilter: 'blur(4px)',
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#339999" strokeWidth="2.5">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/>
        </svg>
        <span style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 600,
          fontSize: '11px', color: '#ffffff', letterSpacing: '0.05em',
        }}>
          View detail
        </span>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// BENTO CARD
// ─────────────────────────────────────────────
const BentoCard = ({ card, index, large = false, onScreenshotClick }) => {
  const Icon = card.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.23, 1, 0.32, 1] }}
      whileHover={{
        y: -4,
        borderColor: '#99f6e4',
        boxShadow: '0 12px 40px rgba(51,153,153,0.12), 0 2px 12px rgba(0,0,0,0.06)',
        transition: { duration: 0.25 },
      }}
      style={{
        background: '#ffffff', border: '1.5px solid #e2e8f0',
        borderRadius: '20px', padding: large ? '28px' : '24px',
        boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
        display: 'flex', flexDirection: 'column', gap: '20px',
        height: '100%', cursor: 'default',
        transition: 'border-color 0.3s ease',
      }}
    >
      <ScreenshotFrame
        src={card.screenshot} alt={card.screenshotAlt}
        large={large} onClick={() => onScreenshotClick(card)}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '8px',
            background: 'linear-gradient(135deg, #00022e, #02075d)',
            border: '1px solid rgba(51,153,153,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon style={{ width: '13px', height: '13px', color: '#ffffff' }} />
          </div>
          <span style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 700,
            fontSize: '11px', letterSpacing: '0.15em',
            textTransform: 'uppercase', color: '#339999',
          }}>
            {card.label}
          </span>
        </div>
        <h3 style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 900,
          fontSize: large ? '22px' : '18px', color: '#0f172a',
          letterSpacing: '-0.02em', lineHeight: 1.25, margin: 0,
        }}>
          {card.title}
        </h3>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 400,
          fontSize: '14px', color: '#475569',
          lineHeight: 1.65, margin: 0,
        }}>
          {card.description}
        </p>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────
// PROOF SECTION
// ─────────────────────────────────────────────
export const ProofSection = () => {
  const [lightboxCard, setLightboxCard] = useState(null);
  const openLightbox = useCallback((card: typeof CARDS[0]) => {
    setLightboxCard(card);
  }, []);
  const closeLightbox = useCallback(() => setLightboxCard(null), []);

  useEffect(() => {
    CARDS.forEach(card => {
      const img = new Image();
      img.src = card.screenshot;
    });
  }, []);

  return (
    <section
      className="w-full py-24 px-6 md:px-12"
      style={{ background: '#f2f2f2', fontFamily: 'Inter, sans-serif' }}
    >
      {/* Inject CSS transitions once */}
      <style>{LIGHTBOX_STYLES}</style>

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          >
            <h2 style={{
              fontFamily: 'Inter, sans-serif', fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: '#0f172a',
              letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '0',
            }}>
              Everything the Auditor Needs.
              <br />
              <span style={{ color: '#339999' }}>In One Click.</span>
            </h2>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
              style={{
                height: '3px', width: '48px', borderRadius: '99px',
                background: 'linear-gradient(90deg, #339999, #06b6d4)',
                margin: '20px auto 0', transformOrigin: 'left center',
              }}
            />
          </motion.div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5" style={{ gap: '16px', alignItems: 'stretch' }}>
          <div className="lg:col-span-3" style={{ minHeight: '100%' }}>
            <BentoCard card={CARDS[0]} index={0} large={true} onScreenshotClick={openLightbox} />
          </div>
          <div className="lg:col-span-2 grid grid-cols-1" style={{ gap: '16px' }}>
            <BentoCard card={CARDS[1]} index={1} large={false} onScreenshotClick={openLightbox} />
            <BentoCard card={CARDS[2]} index={2} large={false} onScreenshotClick={openLightbox} />
          </div>
        </div>

        {/* Bottom close */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center mt-16"
          style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#94a3b8' }}
        >
          Everything timestamped. Everything locked. Everything ready.
        </motion.p>
      </div>

      {/* Lightbox — CSS transitions, no Framer Motion dependency */}
      {lightboxCard && (
        <Lightbox
          key={lightboxCard.id}
          card={lightboxCard}
          onClose={closeLightbox}
        />
      )}
    </section>
  );
};

export default ProofSection;
